import { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  type DateRange,
  type RangePresetId,
  PRESET_IDS,
  endOfDay,
  formatRangeLabel,
  matchPreset,
  monthGrid,
  presetRange,
  sameDay,
  shiftRange,
  startOfDay,
  startOfMonth,
} from '../features/kronos/reportDateRange';
import { useTranslation } from 'react-i18next';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Injectable for tests; defaults to now. */
  today?: Date;
  locale?: string;
}

export function DateRangePicker({ value, onChange, today, locale = 'fr-FR' }: DateRangePickerProps) {
  const { t } = useTranslation();
  const presetLabel = (id: RangePresetId) => t(`common.dateRange.presets.${id}`);
  const now = useMemo(() => today ?? new Date(), [today]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Left-hand month shown in the calendar (right shows the following month).
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(value.from));
  // First click of an in-progress custom selection (null when idle).
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const open = Boolean(anchorEl);
  const activePreset = matchPreset(value, now);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setViewMonth(startOfMonth(value.from));
    setPendingStart(null);
    setHoverDate(null);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const applyPreset = (id: RangePresetId) => {
    onChange(presetRange(id, now));
    handleClose();
  };

  const handleDayClick = (date: Date) => {
    if (pendingStart === null) {
      setPendingStart(startOfDay(date));
      return;
    }
    // Second click completes the range (swap if reversed).
    const a = pendingStart;
    const b = startOfDay(date);
    const [lo, hi] = a.getTime() <= b.getTime() ? [a, b] : [b, a];
    onChange({ from: startOfDay(lo), to: endOfDay(hi) });
    setPendingStart(null);
    setHoverDate(null);
    handleClose();
  };

  // The range to paint: committed value, or the in-progress selection preview.
  const previewRange: DateRange = useMemo(() => {
    if (pendingStart && hoverDate) {
      const b = startOfDay(hoverDate);
      const [lo, hi] = pendingStart.getTime() <= b.getTime() ? [pendingStart, b] : [b, pendingStart];
      return { from: lo, to: hi };
    }
    if (pendingStart) return { from: pendingStart, to: pendingStart };
    return value;
  }, [pendingStart, hoverDate, value]);

  const inRange = (d: Date) =>
    startOfDay(d).getTime() >= startOfDay(previewRange.from).getTime() &&
    startOfDay(d).getTime() <= startOfDay(previewRange.to).getTime();
  const isEndpoint = (d: Date) =>
    sameDay(d, previewRange.from) || sameDay(d, previewRange.to);

  return (
    <>
      <Stack direction="row" sx={{ alignItems: 'center' }} spacing={0.5}>
        <Tooltip title={t('common.dateRange.prevPeriod')}>
          <IconButton
            size="small"
            aria-label={t('common.dateRange.prevPeriod')}
            onClick={() => onChange(shiftRange(value, -1))}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <ButtonBase
          onClick={handleOpen}
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: '6px',
            border: '1px solid',
            borderColor: 'divider',
            gap: 1,
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16 }} />
          {formatRangeLabel(value, locale)}
        </ButtonBase>
        <Tooltip title={t('common.dateRange.nextPeriod')}>
          <IconButton
            size="small"
            aria-label={t('common.dateRange.nextPeriod')}
            onClick={() => onChange(shiftRange(value, 1))}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { role: 'dialog', 'aria-label': t('common.dateRange.selectAria') } }}
      >
        <Stack direction="row">
          {/* Preset sidebar */}
          <List dense sx={{ minWidth: 160, borderRight: '1px solid', borderColor: 'divider', py: 1 }}>
            {PRESET_IDS.map((id) => (
              <ListItemButton
                key={id}
                selected={activePreset === id}
                onClick={() => applyPreset(id)}
              >
                <ListItemText slotProps={{ primary: { sx: { fontSize: '13px' } } }} primary={presetLabel(id)} />
              </ListItemButton>
            ))}
          </List>

          {/* Dual-month calendar */}
          <Box sx={{ p: 2 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton
                size="small"
                aria-label={t('common.dateRange.prevMonth')}
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Stack direction="row" spacing={6}>
                <MonthLabel month={viewMonth} locale={locale} />
                <MonthLabel
                  month={new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1)}
                  locale={locale}
                />
              </Stack>
              <IconButton
                size="small"
                aria-label={t('common.dateRange.nextMonth')}
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={3}>
              <MonthGrid
                month={viewMonth}
                inRange={inRange}
                isEndpoint={isEndpoint}
                onDayClick={handleDayClick}
                onDayHover={setHoverDate}
              />
              <MonthGrid
                month={new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1)}
                inRange={inRange}
                isEndpoint={isEndpoint}
                onDayClick={handleDayClick}
                onDayHover={setHoverDate}
              />
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
}

function MonthLabel({ month, locale }: { month: Date; locale: string }) {
  return (
    <Typography sx={{ fontWeight: 700, fontSize: '13px', minWidth: 130, textAlign: 'center' }}>
      {month.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
    </Typography>
  );
}

interface MonthGridProps {
  month: Date;
  inRange: (d: Date) => boolean;
  isEndpoint: (d: Date) => boolean;
  onDayClick: (d: Date) => void;
  onDayHover: (d: Date | null) => void;
}

function MonthGrid({ month, inRange, isEndpoint, onDayClick, onDayHover }: MonthGridProps) {
  const { t } = useTranslation();
  const cells = monthGrid(month);
  return (
    <Box role="grid" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 32px)', gap: 0 }}>
      {WEEKDAY_KEYS.map((key) => (
        <Box
          key={key}
          sx={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'text.secondary', pb: 0.5 }}
        >
          {t(`common.dateRange.weekdays.${key}`)}
        </Box>
      ))}
      {cells.map((cell) => {
        const selected = isEndpoint(cell.date) && cell.inCurrentMonth;
        const within = inRange(cell.date) && cell.inCurrentMonth;
        return (
          <ButtonBase
            key={cell.date.toISOString()}
            role="gridcell"
            // Out-of-month cells are decorative padding — hide them from the
            // a11y tree so day numbers stay unique per grid (June's trailing
            // July cells would otherwise duplicate days 1–12).
            aria-label={cell.inCurrentMonth ? String(cell.date.getDate()) : undefined}
            aria-hidden={cell.inCurrentMonth ? undefined : true}
            disabled={!cell.inCurrentMonth}
            onClick={() => onDayClick(cell.date)}
            onMouseEnter={() => onDayHover(cell.date)}
            sx={{
              width: 32,
              height: 32,
              fontSize: 12,
              borderRadius: selected ? '6px' : 0,
              color: !cell.inCurrentMonth
                ? 'text.disabled'
                : selected
                  ? 'primary.contrastText'
                  : 'text.primary',
              bgcolor: selected ? 'primary.main' : within ? 'primary.light' : 'transparent',
              opacity: cell.inCurrentMonth ? 1 : 0,
              '&:hover': { bgcolor: selected ? 'primary.main' : 'action.hover' },
            }}
          >
            {cell.date.getDate()}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
